import { Option } from '@sapphire/result';

import { NotificationAction, NotificationItem } from '@ps/notification-models';

import {
  NotificationActionApplyResult,
  NotificationActionLiveState,
} from './notification.model';

function idValue(value: unknown): Option<string> {
  if (typeof value === 'number' || (typeof value === 'string' && !!value)) {
    return Option.some(`${value}`);
  }
  return Option.none;
}

function liveStateKey(commentId: string, actionId: string): string {
  return `${commentId}:${actionId}`;
}

export function notificationCommentId(
  notification: NotificationItem,
): Option<string> {
  return idValue(notification.data['commentId']);
}

export function actionSurveyIds(notifications: NotificationItem[]): string[] {
  const surveyIds = notifications
    .filter(
      (notification) =>
        notification.actions.length > 0 &&
        notificationCommentId(notification).isSome(),
    )
    .map((notification) => idValue(notification.data['surveyId']))
    .filter((surveyId) => surveyId.isSome())
    .map((surveyId) => surveyId.unwrap());
  return [...new Set(surveyIds)];
}

export function withResult(
  action: NotificationAction,
  result: NotificationActionApplyResult,
): NotificationAction {
  return {
    ...action,
    state: result.state,
    resultLabel: result.resultLabel,
    appliedBy: result.appliedBy,
    appliedAt: result.appliedAt,
  };
}

export function withLiveStates(
  notifications: NotificationItem[],
  states: NotificationActionLiveState[],
): NotificationItem[] {
  const statesByKey = new Map(
    states.map((liveState) => [
      liveStateKey(liveState.commentId, liveState.actionId),
      liveState,
    ]),
  );
  return notifications.map((notification) =>
    notificationCommentId(notification).match({
      some: (commentId) => ({
        ...notification,
        actions: notification.actions.map((action) =>
          Option.from(
            statesByKey.get(liveStateKey(commentId, action.actionId)),
          ).match({
            some: ({ config, result }) => ({
              ...withResult(action, result),
              config,
            }),
            none: () => action,
          }),
        ),
      }),
      none: () => notification,
    }),
  );
}

// The read API only holds the publish-time snapshot, so a reload must not undo a known result.
export function withKnownActionStates(
  incoming: NotificationItem[],
  known: NotificationItem[],
): NotificationItem[] {
  const knownById = new Map(
    known.map((notification) => [notification.notificationId, notification]),
  );
  return incoming.map((notification) =>
    Option.from(knownById.get(notification.notificationId)).match({
      some: (knownNotification) => ({
        ...notification,
        actions: notification.actions.map((action) =>
          Option.from(
            knownNotification.actions.find(
              (knownAction) =>
                knownAction.actionId === action.actionId &&
                knownAction.state !== 'pending',
            ),
          ).match({
            some: (knownAction) =>
              action.state === 'pending' ? knownAction : action,
            none: () => action,
          }),
        ),
      }),
      none: () => notification,
    }),
  );
}
